import * as Tone from 'tone'
import { InstrumentBase } from './InstrumentBase';
import { getAudioGraphInstance } from '../audioEngine';
import { addKeysAsField } from '../utils/misc';
import { PianoSoundFontUI } from '../projectViews/stationViews/PianoSoundFontUI.jsx';
import { PianoRoller } from './PianoRoller.js';
import colors from "../../styles/colors.module.scss";
import WebAudioFontPlayer from 'webaudiofontplayer';


export class PianoSoundFont extends InstrumentBase {
    static metadata = {
        tipus:  'piano_soundfont',
        tipusDisplay:  'Piano',
        versio:  '0.1',
        color:  colors.orange,
        hasLogo:  false,
        hasMiniature:  false,
    }
    static parametersDescription = addKeysAsField({
        ...super.parametersDescription,
        ...PianoRoller.parametersDescription,
        sound: {type: 'enum', label:'Tipus de so', options: ['Acústic', 'Elèctric 1', 'Elèctric 2'], initial: 'Acústic', bind: "preset"},
    }, "nom");
    static pitchRange = {
        ...super.pitchRange,
        typical: {min: 48, max: 71}
    }

    soundfontsInfo = {
        'Acústic': {'url': appPrefix + '/static/dist/public/soundfonts/0000_Full_Grand_Piano-slim.json', 'gain': -6},
        'Elèctric 1': {'url': appPrefix + '/static/dist/public/soundfonts/0380_rhodes-instrument-0-38.json', 'gain': -9}, 
        'Elèctric 2': {'url': appPrefix + '/static/dist/public/soundfonts/0000_Electric Piano 26.json', 'gain': -12}
    }
    soundFontsData = {}
    defaultSoundFont = 'Elèctric 1'
    soundFontLoaded = false;
    soundFontIsLoading = false;
    currentlyPlayedNoteEvents = {};

    getUserInterfaceComponent() {
        return PianoSoundFontUI
    }

    async loadSoundFont(soundFontUrl) {
        if (!Object.keys(this.audioNodes).length) { return };
        this.soundFontIsLoading = true;
        let soundFont;
        if (this.soundFontsData[soundFontUrl]) {
            soundFont = this.soundFontsData[soundFontUrl];
        } else {
            const response = await fetch(soundFontUrl);
            if (!response.ok) {
                this.soundFontLoaded = false;
                this.soundFontIsLoading = false;
                throw new Error(`Unable to load soundfont: ${response.status} ${response.statusText}`);
            }
            soundFont = await response.json();
            this.soundFontsData[soundFontUrl] = soundFont;
        }
        this.audioNodes.sfPlayer.setPreset(soundFont);
        this.audioNodes.gainCompensation.gain.value = Tone.dbToGain(this.soundfontsInfo[this.defaultSoundFont].gain);
        this.soundFontLoaded = true;
        this.soundFontIsLoading = false;
    }

    async buildInstrumentAudioGraph() {
        await super.buildInstrumentAudioGraph();
        const response = await fetch(this.soundfontsInfo[this.defaultSoundFont].url);
        if (!response.ok) {
            throw new Error(`Unable to load soundfont: ${response.status} ${response.statusText}`);
        }
        const soundFont = await response.json();

        const gainOutput = new Tone.Gain(Tone.dbToGain(0));
        const gainCompensation = new Tone.Gain(Tone.dbToGain(this.soundfontsInfo[this.defaultSoundFont].gain));
        const player = await WebAudioFontPlayer.load(
            soundFont,
            Tone.getContext().rawContext,
            gainCompensation
        );
        this.soundFontLoaded = true;
        this.audioNodes = {
            gainOutput: gainOutput,
            gainCompensation: gainCompensation,
            sfPlayer: player,
        }
        Tone.connect(this.audioNodes.gainCompensation, this.audioNodes.gainOutput);

        // Crea els nodes d'efectes (això també els afegirà al diccionari de nodes de l'instrument)
        this.addEffectChainNodes(this.audioNodes.gainOutput, this.bus);
    }

    setParameterInAudioGraph(name, value) {
        switch (name) {
            case "sound":
                const soundFontUrl = this.soundfontsInfo[value].url;
                if (soundFontUrl) {
                    this.loadSoundFont(soundFontUrl);
                    this.audioNodes.gainCompensation.gain.value = Tone.dbToGain(this.soundfontsInfo[value].gain);
                }
        }
    }

    onSequencerStep(currentMainSequencerStep, time) {
        if (this.getPlaybackParameterValue("clipNumber") === undefined) return;
        if (!Object.keys(this.audioNodes).length) { return };
        if (!this.soundFontLoaded) return;
        
        // Iterate over all the notes in the sequence and trigger those that start in the current beat (step)
        const currentStep = currentMainSequencerStep % this.getPlaybackClip().length;
        const sequence = this.getPlaybackClip().sequence;
        for (let i = 0; i < sequence.length; i++) {
            const note = sequence[i];
            if (note.onset === currentStep) {
                const duration = Math.min(note.onset + note.duration, this.getPlaybackClip().length) - note.onset;
                this.audioNodes.sfPlayer.queueWaveTable(
                    time,
                    note.pitch,
                    duration * getAudioGraphInstance().get16BeatTime(),
                    (note.velocity ?? 127) / 127
                );
            }
        }   
    }
    
    onTransportStop() {
        if (!Object.keys(this.audioNodes).length) { return };
        this.audioNodes.sfPlayer.cancelQueue();
    }
    
    onStopAllSounds() {
        if (!Object.keys(this.audioNodes).length) { return };
        this.audioNodes.sfPlayer.cancelQueue();
    }
    
    onMidiNote(pitch, midiVelocity=127, noteOff, extras) {
        if (!getAudioGraphInstance().isGraphBuilt()) return; 
        if (!Object.keys(this.audioNodes).length) { return };
        if (!this.soundFontLoaded) return;
        if (!noteOff) {
            this.clearNoteEventIfExisting(pitch);
            const noteEvt = this.audioNodes.sfPlayer.queueWaveTable(
                    Tone.getContext().currentTime,
                    pitch,
                    36 * getAudioGraphInstance().get16BeatTime(),  // Very long note time, we'll cancel it with a reference to the note event
                    (midiVelocity ?? 127) / 127
                );
            this.currentlyPlayedNoteEvents[pitch] = noteEvt;
        } else {
            this.clearNoteEventIfExisting(pitch);
        }
        if (!extras.skipRecording) this.handlePianoRollRecording(pitch, midiVelocity, noteOff);
    }

    clearNoteEventIfExisting(pitch) {
        const noteEvt = this.currentlyPlayedNoteEvents[pitch];
        if (noteEvt) {
            noteEvt.cancel();
            delete this.currentlyPlayedNoteEvents[pitch];
        }
    }
}